import { ColumnDef, Row } from '@tanstack/react-table'
import React, { useMemo } from 'react'

import { PSComponent } from '../../models/PSComponent'
import { ReportResult } from '../../models/ReportResult'
import { ReportTotal } from '../../models/ReportTotal'
import DataTable from '../app/design/DataTable'
import EmptyState from '../app/design/EmptyState'
import { DeltaSubtitle } from './ReportDetailsPage'
import { ReportIcon } from './ReportIcon'
import { ShowReportDetailsProps } from './ReportRunnerPage'

export type TableRowsType = {
    [key: string]: ReportResult[]
}

export type TableSubrowsType = {
    [key: string]: ReportResult[][]
}

export type TableColumnsType = {
    [key: string]: ReportTotal
}

export type TableRowSortedKeysType = {
    results: string[]
    subresults: string[][]
}

type Props = {
    measures: PSComponent[]
    activeMeasure: PSComponent
    reportResultRows: TableRowsType
    reportSubresultRows: TableSubrowsType
    resultSorting: TableRowSortedKeysType
    groupByEligible: boolean
    reportTotalColumns: TableColumnsType
    showDetail: (props: ShowReportDetailsProps) => void
}

type RowTitle = {
    text: string
    icon?: React.ReactElement
}

type RowData = {
    id: string
    title?: RowTitle
    columns: { [key: string]: ReportResult }
    superColumns?: { [key: string]: ReportResult }
    totalColumns: { [key: string]: ReportTotal }
    subItems?: RowData[]
}

const TitleCell = ({ title }: { title?: RowTitle }) => {
    if (title !== undefined) {
        const { text, icon } = title
        return (
            <div className="rowTitle">
                {icon !== undefined && <div className="icon">{icon}</div>}
                <div className="text-[15px] font-bold text-gray-700">{text}</div>
            </div>
        )
    }
    return <div className="rowTitle"></div>
}

const DataCell = ({
    measure,
    totalData,
    showDetail,
    resultData,
    superResultData,
}: {
    measure: PSComponent
    totalData?: ReportTotal
    showDetail: (props: ShowReportDetailsProps) => void
    resultData?: ReportResult
    superResultData?: ReportResult
}) => {
    if (totalData === undefined) {
        return <div className="dataCell empty"></div>
    }
    const reportTotal = resultData?.total ?? totalData
    const clickable = reportTotal !== undefined
    const onClick = () => {
        if (clickable) {
            const values = !!resultData
                ? !!superResultData
                    ? [superResultData, resultData]
                    : [resultData]
                : []
            showDetail({ selectedMeasure: measure, measureTotal: totalData, values })
        }
    }

    return (
        <div className={`dataCell ${resultData === undefined ? 'font-bold' : ''}`}>
            <div
                className={`text-sm text-gray-700 ${clickable ? 'cursor-pointer! underline!' : ''}`}
                onClick={onClick}
            >
                {reportTotal?.totalString}
            </div>
            {resultData !== undefined && !!reportTotal?.totalString && (
                <DeltaSubtitle total={reportTotal} onlyReturnPercent={true} />
            )}
        </div>
    )
}

export const ReportDataTable = ({
    measures,
    activeMeasure,
    reportResultRows,
    reportSubresultRows,
    resultSorting,
    groupByEligible,
    reportTotalColumns,
    showDetail,
}: Props) => {
    const titleColumn: ColumnDef<RowData> = {
        accessorKey: 'title',
        header: () => <div className="text-left font-normal! text-gray-600">Item type</div>,
        cell: ({ row }) => <TitleCell title={row.original.title} />,
    }

    const columns = useMemo(
        (): ColumnDef<RowData>[] => [
            titleColumn,
            ...measures.map((measure) => ({
                accessorKey: measure.id!,
                header: () => (
                    <div
                        className={`text-right text-gray-600 ${measure.id === activeMeasure.id ? 'font-black!' : 'font-normal!'}`}
                    >
                        {measure.title}
                    </div>
                ),
                cell: ({ row }: { row: Row<RowData> }) => (
                    <DataCell
                        measure={measure}
                        totalData={row.original.totalColumns[measure.id!]}
                        showDetail={showDetail}
                        resultData={row.original.columns[measure.id!]}
                        superResultData={row.original.superColumns?.[measure.id!]}
                    />
                ),
            })),
        ],
        [measures, activeMeasure, showDetail]
    )

    const tableData: RowData[] = useMemo(() => {
        const rowIndex: { [key: string]: number } = {}
        const rowSubIndex: { [key: string]: { [key: string]: number } | undefined } = {}
        const newTableData: RowData[] = []
        const totalRowId = 'total_row_id'

        // if the table has at least one result, add an empty title row
        if (resultSorting.results.length) {
            // if there are results, add a total row
            rowIndex[totalRowId] = newTableData.length
            newTableData.push({
                id: totalRowId,
                title: { text: 'Total' },
                columns: {},
                totalColumns: {},
            })
        }

        // add empty rows for each result and subresult (if exists)
        for (let ii = 0; ii < resultSorting.results.length; ++ii) {
            const id = resultSorting.results[ii]
            rowIndex[id] = newTableData.length
            const row: RowData = {
                id: id,
                title: undefined,
                columns: {},
                totalColumns: {},
            }
            newTableData.push(row)
            const subIDs = resultSorting.subresults[ii] ?? []
            if (subIDs.length > 0) {
                row.subItems = []
                rowSubIndex[id] = {}
                for (const subID of subIDs) {
                    rowSubIndex[id]![subID] = row.subItems.length
                    const subRow: RowData = {
                        id: subID,
                        title: undefined,
                        columns: {},
                        superColumns: {},
                        totalColumns: {},
                    }
                    row.subItems.push(subRow)
                }
            }
        }

        // fill in the rows with data
        for (const measure of measures) {
            const measureID = measure.id!
            const total = reportTotalColumns[measureID]
            const results = reportResultRows[measureID] ?? []
            const subResults = reportSubresultRows[measureID] ?? []

            // fill the title row with the total data
            const titleIdx = rowIndex[totalRowId]
            if (titleIdx !== undefined) {
                newTableData[titleIdx].totalColumns[measureID] = total
            }

            // fill the result and subresult rows with the data
            for (let ii = 0; ii < results.length; ++ii) {
                const result = results[ii]
                const resultID = result.id ?? ''
                const idx = rowIndex[resultID]
                const row = newTableData[idx]
                if (row) {
                    if (!row?.title) {
                        row.title = {
                            text: result.title!,
                            icon: <ReportIcon result={result} />,
                        }
                    }
                    row.columns[measureID] = result
                    row.totalColumns[measureID] = total
                    if (row?.subItems !== undefined) {
                        for (let jj = 0; jj < subResults[ii].length; ++jj) {
                            const subResult = subResults[ii][jj]
                            const subRow = row.subItems[rowSubIndex[resultID]![subResult.id ?? '']]
                            if (subRow !== undefined) {
                                if (!subRow.title) {
                                    subRow.title = { text: subResult.title! }
                                }
                                subRow.columns[measureID] = subResult
                                subRow.superColumns![measureID] = result
                                subRow.totalColumns[measureID] = total
                            }
                        }
                    }
                }
            }
        }

        return newTableData
    }, [reportResultRows, reportSubresultRows, resultSorting, measures])

    return (
        <DataTable
            columns={columns}
            data={tableData}
            emptyState={
                <EmptyState
                    title={groupByEligible ? 'No data available' : 'Group not eligible'}
                    message={
                        groupByEligible
                            ? 'Try changing dimensions, metrics or filters.'
                            : 'Change the grouping to view your data.'
                    }
                />
            }
            getSubRows={(row) => row.subItems}
            expandable
            pinFirstColumn
        />
    )
}
